import React from 'react';

/**
 * Small inline-SVG icon set (Oct 2026). Replaces Font Awesome class names
 * ("fas fa-…"), which rendered blank because the icon font is never loaded.
 * Stroke icons on a 16px grid; color follows currentColor.
 */
export type IconName =
  | 'info' | 'ban' | 'chevron-up' | 'chevron-down' | 'alert' | 'plus' | 'search'
  | 'spinner' | 'filter' | 'alert-circle' | 'grid' | 'sync' | 'calendar';

const PATHS: Record<IconName, React.ReactNode> = {
  info: (<><circle cx="8" cy="8" r="6.25" /><path d="M8 7.2v3.6M8 5.2v.1" /></>),
  ban: (<><circle cx="8" cy="8" r="6.25" /><path d="M3.6 3.6l8.8 8.8" /></>),
  'chevron-up': <path d="M4 10l4-4 4 4" />,
  'chevron-down': <path d="M4 6l4 4 4-4" />,
  alert: (<><path d="M8 1.8 15 14H1L8 1.8Z" /><path d="M8 6.2v3.6M8 11.6v.1" /></>),
  plus: <path d="M8 3v10M3 8h10" />,
  search: (<><circle cx="7" cy="7" r="4.5" /><path d="M10.4 10.4 14 14" /></>),
  spinner: <path d="M8 1.75A6.25 6.25 0 1 1 1.75 8" />,
  filter: <path d="M2 3h12l-4.5 5.5V13l-3-1.5V8.5L2 3Z" />,
  'alert-circle': (<><circle cx="8" cy="8" r="6.25" /><path d="M8 4.8v3.8M8 10.8v.1" /></>),
  grid: (<><rect x="2" y="2" width="5" height="5" rx="1" /><rect x="9" y="2" width="5" height="5" rx="1" /><rect x="2" y="9" width="5" height="5" rx="1" /><rect x="9" y="9" width="5" height="5" rx="1" /></>),
  sync: (<><path d="M13.5 6A5.75 5.75 0 0 0 3 4.5M2.5 10A5.75 5.75 0 0 0 13 11.5" /><path d="M3 1.8v2.9h2.9M13 14.2v-2.9h-2.9" /></>),
  calendar: (<><rect x="2" y="3" width="12" height="11" rx="1.5" /><path d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3" /></>),
};

interface IconProps {
  name: IconName;
  size?: number;
  title?: string;
  className?: string;
  style?: React.CSSProperties;
  spin?: boolean;
}

const Icon: React.FC<IconProps> = ({ name, size = 14, title, className, style, spin }) => (
  <svg
    viewBox="0 0 16 16"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`${spin ? 'icon-spin ' : ''}${className ?? ''}`.trim() || undefined}
    style={{ flex: 'none', verticalAlign: '-2px', ...style }}
    role={title ? 'img' : undefined}
    aria-hidden={title ? undefined : true}
    aria-label={title}
  >
    {title && <title>{title}</title>}
    {PATHS[name]}
  </svg>
);

export default Icon;
